/**
 * Dashboard page.
 * Renders verdict trend charts filtered by API key(s) and time interval.
 *
 * @module pages/dashboard
 */

import React, { useState,
                useEffect,
                useCallback }    from 'react';
import { useSearchParams }       from 'react-router-dom';
import { RefreshCw }             from 'lucide-react';
import Layout                    from '../components/Layout';
import MultiSelectComboBox       from '../components/MultiSelectComboBox';
import VerdictChart              from '../components/VerdictChart';
import TokenChart                from '../components/TokenChart';
import { useI18n }               from '../dataProviders/I18nProvider';
import apiClient                 from '../procedural/apiClient';
import * as constants            from '../constants';

/**
 * Renders the Dashboard page with verdict trend charts.
 *
 * @returns {React.ReactElement}
 */
export default function Dashboard() {
  const { t }                           = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [apiKeys, setApiKeys]                   = useState([]);
  const [selectedKeyIds, setSelectedKeyIds]     = useState([]);
  const [chartDataByKey, setChartDataByKey]     = useState({});
  const [tokenDataByKey, setTokenDataByKey]     = useState({});
  const [rangeStart, setRangeStart]             = useState(null);
  const [rangeEnd, setRangeEnd]                 = useState(null);
  const [tokenRangeStart, setTokenRangeStart]   = useState(null);
  const [tokenRangeEnd, setTokenRangeEnd]       = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);

  const tabParam = searchParams.get('tab');
  const interval = constants.TIME_INTERVALS.includes(tabParam) ? tabParam : 'hour';

  const tzParam      = searchParams.get('tz');
  const useLocalTime = tzParam === 'local';

  // -- Load API keys on mount ------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function loadKeys() {
      try {
        const res = await apiClient.get('/api/api-keys');
        if (!cancelled) {
          setApiKeys(res.data.apiKeys);
          const ids = res.data.apiKeys.map(k => k.id);
          setSelectedKeyIds(ids);
        }
      } catch {
        if (!cancelled) setError(t('dashboard.error.loadKeys'));
      }
    }

    loadKeys();
    return () => { cancelled = true; };
  }, [t]);

  // -- Fetch verdicts when selection or interval changes ---------------------

  const fetchVerdicts = useCallback(async (keyIds, ivl) => {
    if (keyIds.length === 0) {
      setChartDataByKey({});
      setTokenDataByKey({});
      setRangeStart(null);
      setRangeEnd(null);
      setTokenRangeStart(null);
      setTokenRangeEnd(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const qs = `interval=${ivl}&apiKeyIds=${keyIds.join(',')}`;

      const [verdictRes, tokenRes] = await Promise.all([
        apiClient.get(`/api/dashboard/verdicts?${qs}`),
        apiClient.get(`/api/dashboard/tokens?${qs}`),
      ]);

      const verdicts = verdictRes.data.verdicts;
      const grouped  = {};
      for (const id of keyIds) grouped[id] = [];
      for (const row of verdicts) {
        const key = row.apiKeyId || '_all';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      }
      setChartDataByKey(grouped);
      setRangeStart(verdictRes.data.rangeStart ? new Date(verdictRes.data.rangeStart).getTime() : null);
      setRangeEnd(verdictRes.data.rangeEnd     ? new Date(verdictRes.data.rangeEnd).getTime()   : null);

      const tokens       = tokenRes.data.tokens;
      const tokenGrouped = {};
      for (const id of keyIds) tokenGrouped[id] = [];
      for (const row of tokens) {
        const key = row.apiKeyId || '_all';
        if (!tokenGrouped[key]) tokenGrouped[key] = [];
        tokenGrouped[key].push(row);
      }
      setTokenDataByKey(tokenGrouped);
      setTokenRangeStart(tokenRes.data.rangeStart ? new Date(tokenRes.data.rangeStart).getTime() : null);
      setTokenRangeEnd(tokenRes.data.rangeEnd     ? new Date(tokenRes.data.rangeEnd).getTime()   : null);
    } catch {
      setError(t('dashboard.error.loadVerdicts'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (apiKeys.length > 0 && selectedKeyIds.length > 0) {
      fetchVerdicts(selectedKeyIds, interval);
    }
  }, [selectedKeyIds, interval, apiKeys.length, fetchVerdicts]);

  // -- Handlers --------------------------------------------------------------

  function setParam(key, value) {
    const next = Object.fromEntries(searchParams.entries());
    next[key]  = value;
    setSearchParams(next, { replace: true });
  }

  function handleIntervalChange(ivl) {
    setParam('tab', ivl);
  }

  function handleTimezoneChange(tz) {
    setParam('tz', tz);
  }

  function handleRefresh() {
    fetchVerdicts(selectedKeyIds, interval);
  }

  // -- Render ----------------------------------------------------------------

  const keyOptions = apiKeys.map(k => ({ value: k.id, label: k.description }));

  const selectedLabel = selectedKeyIds.length > 0
    ? t('dashboard.selected', { count: selectedKeyIds.length })
    : undefined;

  return (
    <Layout title={t('page.dashboard')}>
      <div className="dashboard-container">
      <div className="dashboard-controls">
        <div className="dashboard-control-group">
          <span className="dashboard-control-label">{t('dashboard.apiKeys.label')}</span>
          <MultiSelectComboBox
            options={keyOptions}
            selected={selectedKeyIds}
            onChange={setSelectedKeyIds}
            placeholder={t('dashboard.apiKeys.placeholder')}
            selectedLabel={selectedLabel}
          />
        </div>

        <div className="dashboard-control-group">
          <span className="dashboard-control-label">{t('dashboard.timeInterval.label')}</span>
          <div className="dashboard-interval-tabs">
            {constants.TIME_INTERVALS.map(ivl => (
              <button
                key={ivl}
                type="button"
                className={`dashboard-interval-tab${interval === ivl ? ' dashboard-interval-tab--active' : ''}`}
                onClick={() => handleIntervalChange(ivl)}
              >
                {t(`dashboard.interval.${ivl}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-control-group">
          <span className="dashboard-control-label">{t('dashboard.timezone.label')}</span>
          <div className="dashboard-interval-tabs">
            <button
              type="button"
              className={`dashboard-interval-tab${!useLocalTime ? ' dashboard-interval-tab--active' : ''}`}
              onClick={() => handleTimezoneChange('utc')}
            >
              {t('dashboard.timezone.utc')}
            </button>
            <button
              type="button"
              className={`dashboard-interval-tab${useLocalTime ? ' dashboard-interval-tab--active' : ''}`}
              onClick={() => handleTimezoneChange('local')}
            >
              {t('dashboard.timezone.local')}
            </button>
          </div>
        </div>

        <button
          type="button"
          className="dashboard-refresh-btn"
          title={t('dashboard.refresh')}
          onClick={handleRefresh}
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {error && <p className="dashboard-error">{error}</p>}

      {apiKeys.length === 0 && !error && (
        <p className="dashboard-no-data">{t('dashboard.noKeys')}</p>
      )}

      {loading && <p className="dashboard-loading">Loading&hellip;</p>}

      {!loading && selectedKeyIds.length > 0 && (() => {
        const charts = selectedKeyIds
          .flatMap(id => {
            const key        = apiKeys.find(k => k.id === id);
            if (!key) return [];

            const verdictData = chartDataByKey[id]  || [];
            const tokenData   = tokenDataByKey[id]  || [];

            const elements = [];

            if (verdictData.length > 0) {
              elements.push(
                <VerdictChart
                  key={`verdict-${id}`}
                  data={verdictData}
                  apiKeyDescription={key.description}
                  interval={interval}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  useLocalTime={useLocalTime}
                />,
              );
            }

            if (tokenData.length > 0) {
              elements.push(
                <TokenChart
                  key={`token-${id}`}
                  data={tokenData}
                  apiKeyDescription={key.description}
                  interval={interval}
                  rangeStart={tokenRangeStart}
                  rangeEnd={tokenRangeEnd}
                  useLocalTime={useLocalTime}
                />,
              );
            }

            return elements;
          });

        return charts.length > 0
          ? charts
          : <p className="dashboard-no-data">{t('dashboard.noData')}</p>;
      })()}
      </div>
    </Layout>
  );
}
